// Libraries
import { useEffect } from 'react';
import * as d3 from 'd3';

// Components
import { theme, pollFillScale, pollStrokeScale, rScale, 
         equityIndicatorScale,
         sizeData, equityIndicatorData, colorData,
         changeNoPollsThresholdScale,
         baseYearCVAPPerPollThresholdScale,
         overallChangeThresholdScale,
         statusThresholdScale,
         changeYearCVAPPerPollThresholdScale,
         overallChangeStrokeThresholdScale,
         pollStrokeScale2,
         changeYearCVAPPerPollStrokeThresholdScale } from "../utils/Theme";

import { IndicatorSizeLabel, IndicatorColorLabel, EquityIndicatorLabel } from "../utils/Global";
import WarningIcon from '@mui/icons-material/Warning';

import { CVAPListener, baseYearDescr, lastGeYearDescr } from './Tooltip';
         
// Types
import { EquityIndicator, Indicator, ChangeYear } from '../utils/Types';

const equityLegendId = 'Equity-Legend';
const sizeLegendId = 'Size-Legend';
const pollLegendId = 'Poll-Legend';
const colorSummaryId ="Color-Summary";
const width = 230;
const colorSummaryHeight = 110;

const circleStart = 20;
const textStart = circleStart + 24;
const yStep = 20;
const yStart = 5;
const yAxis = 49;
const legendRadius = 5;
const rectHeight = 8;

function ComponentGroupInner({title, children, className=""}: 
                             {title: string | React.ReactNode, children: React.ReactNode, className?: string}):  JSX.Element {
  return(
      <div className={"ComponentGroupInner "}>
          <h4 className={className}>{title}</h4>
          {children}
      </div>
  )
}

export function UpdateMissingDataWarning ({status, changeYear, stfp}: {status: any, changeYear: ChangeYear, stfp: string}) {

  if (status?.baseYearNoPollsMissingLatLng > 0 || status?.lastGEYearNoPollsMissingLatLng > 0) {
    return(
      <div className="DataWarning">
         <h4>County missing poll location data</h4>
         <div className="Detail">
          <WarningIcon fontSize="small"/>
          {status?.baseYearNoPollsMissingLatLng > 0 && status?.lastGEYearNoPollsMissingLatLng > 0 ? <div><span className="">{status?.baseYearNoPollsMissingLatLng} {status?.baseYearNoPollsMissingLatLng > 1? "polls": "poll"}</span> in {baseYearDescr(stfp, changeYear)} and <span className=""> {status?.lastGEYearNoPollsMissingLatLng} {status?.lastGEYearNoPollsMissingLatLng > 1? "polls": "poll"}</span> in {lastGeYearDescr(stfp, changeYear)}</div>: 
           status?.baseYearNoPollsMissingLatLng > 0 ?  <div><span className="">{status?.baseYearNoPollsMissingLatLng} {status?.baseYearNoPollsMissingLatLng > 1? "polls": "poll"}</span> in {baseYearDescr(stfp, changeYear)}</div>: <div><span className="">{status?.lastGEYearNoPollsMissingLatLng} {status?.lastGEYearNoPollsMissingLatLng > 1? "polls": "poll"}</span> in {lastGeYearDescr(stfp, changeYear)}</div>
          }
          </div>
      </div>
    )
  } else {
    return <></>;
  }
}

// Reusable function to initialize legend
function initLegend(selector: string) {
  d3.select(`#${selector}`)
    .append('svg')
    .attr('width', width);
}

// Calculate legend height according to number of items in the data to represent
function legendHeight(data: any[], margin: number = 4) {
  const height = legendRadius*2 + (data.length - 1)*yStep + margin;
  return height;
}

// Initiate size legend
function updateSizeLegend(pollHover: any, indicator: Indicator, showEquityIndicator: boolean) {

  let threshold: number | undefined = undefined;

  if (pollHover.changeYearData !== undefined ) {
    let pollSummary = pollHover.changeYearData.pollSummary.status;

    if (indicator.variable === "changeNoPolls") {
      threshold = changeNoPollsThresholdScale(pollSummary.changeNoPolls);
    } else {
      threshold = baseYearCVAPPerPollThresholdScale(pollSummary.baseYearCVAPPerPoll);
    }

  } else {
    threshold = undefined;
  }

  let data = sizeData.filter(d => d.indicator === indicator.variable);

  const svg = d3.select(`#${sizeLegendId} svg`)
    .attr('height', legendHeight(data, indicator.variable === "changeNoPolls" ? 32: 50));

  svg
  .selectAll('circle')
  .data(data, (d: any) => d.id)
  .join(
    (enter: any) => enter
      .append('circle')
      .attr('r', (d: any) => rScale(d.threshold))
      .attr('transform', function (d: any, i: any) {
        let x = data.filter(e => e.threshold < d.threshold).map(e => rScale(e.threshold)).reduce((a, b) => a + b, 0);
        return 'translate(' + circleStart + ', ' + (i * 16 + x + rScale(d.threshold) + 3) + ')';
      })
      .attr('fill', (d: any) => showEquityIndicator? '#CCCCCC': theme.grey.secondary) // Add type assertion
      .attr("stroke", (d: any) => showEquityIndicator? theme.grey.secondary: theme.grey.primary) // Add type assertion
      .attr('stroke-width', 1),
    (update: any) => update
      .attr('opacity', (d: any) => d.threshold === threshold || threshold === undefined? 1 : theme.nonHighlightOpacity)
      .attr('fill', (d: any) => showEquityIndicator? '#CCCCCC': theme.grey.secondary) // Add type assertion
      .attr("stroke", (d: any) => showEquityIndicator? theme.grey.secondary: theme.grey.primary) // Add type assertion
  );

  svg
    .selectAll('text')
    .data(data, (d: any) => d.id)
    .join(
      (enter: any) => enter
        .append('text')
        .attr('x', textStart)
        .attr('y', function(d: any, i: any) { 
          let x = data.filter(e => e.threshold < d.threshold).map(e => rScale(e.threshold)).reduce((a, b) => a + b, 0);
          return i * 16 + x + rScale(d.threshold) + 3})
        .text((d: any) => d.label)
        .attr('font-size', theme.fontSize)
        .attr('fill', theme.grey.primary)
        .attr('dominant-baseline', 'middle'),
      (update: any) => update
        .attr('opacity', (d: any) => d.threshold === threshold || threshold === undefined? 1 : theme.nonHighlightOpacity)
    );
}

function getPollHoverStatus(pollHover: any, indicator: Indicator, summary: boolean) {
  let pollHoverStatus: string | undefined = undefined;

  if (pollHover.type === "County" ) {

    if (pollHover.changeYearData === undefined) {
      pollHoverStatus = undefined;
  } else {
    if (indicator.variable === "changeNoPolls") {

      if (summary) {
        pollHoverStatus = pollHover.changeYearData.pollSummary.status.overallChange;
      } else {
        pollHoverStatus = overallChangeThresholdScale(pollHover.changeYearData.pollSummary.status.overallChange).toString();
      }
      
    } else {
      pollHoverStatus = changeYearCVAPPerPollThresholdScale(pollHover.changeYearData.pollSummary.status.changeYearCVAPPerPoll).toString();
    } 
  }
  
  } else if (pollHover.type === "Poll") {
    pollHoverStatus = statusThresholdScale(pollHover.status) as string; // Add type assertion
  } else {
    pollHoverStatus = undefined;
  }
  return pollHoverStatus;
}

// Initiate poll legend
function updatePollLegend(geo: string, pollHover: any, indicator: Indicator, showEquityIndicator?: boolean) {

  let pollHoverStatus = getPollHoverStatus(pollHover, indicator, false);

  let data = colorData.filter(d => d.geo === geo && d.indicator === indicator.variable);

  const svg = d3.select(`#${pollLegendId} svg`)
    .attr('height', legendHeight(data));

  svg
    .selectAll('circle')
    .data(data, (d: any) => d.id)
    .join(
      enter => enter
        .append('circle')
        .attr('r', legendRadius)
        .attr('transform', function (d, i) {
          return 'translate(' + circleStart + ', ' + (i * yStep + legendRadius + 1) + ')';
        })
        .attr('fill', (d: any) => showEquityIndicator && d.overallChange === "0"? '#CCCCCC': pollFillScale(d.overallChange) as string) // Add type assertion
        .attr("stroke", (d: any) => showEquityIndicator && d.overallChange === "0"? theme.grey.secondary: pollStrokeScale(d.status) as string) // Add type assertion
        .attr('stroke-width', 1),
      update => update
        .attr('opacity', (d: any) => d.overallChange === pollHoverStatus || pollHoverStatus === undefined? 1 : theme.nonHighlightOpacity)
        .attr('fill', (d: any) => showEquityIndicator && d.overallChange === "0"? '#CCCCCC': pollFillScale(d.overallChange) as string) // Add type assertion
        .attr("stroke", (d: any) => showEquityIndicator && d.overallChange === "0"? theme.grey.secondary: pollStrokeScale(d.status) as string) // Add type assertion
      );

  svg
  .selectAll('text')
  .data(data, (d: any) => d.id)
  .join(
    (enter: any) => enter
      .append('text')
      .attr('x', textStart)
      .attr('y', (d: any, i: number) => i * yStep + legendRadius + 6)
      .text((d: any) => d.label)
      .attr('font-size', theme.fontSize)
      .attr('fill', theme.grey.primary),
    (update: any) => update
      .attr('opacity', (d: any) => d.overallChange === pollHoverStatus || pollHoverStatus === undefined? 1 : theme.nonHighlightOpacity)
  );
}

// Initiate equity legend
function updateEquityLegend(equityIndicator: EquityIndicator, geoHover: any, geo: string) {

  let fillColor: string | undefined = undefined;

  if (geoHover.changeYearData  !== undefined ) {

    if (geoHover.changeYearData[equityIndicator.variable] !== undefined) {
      fillColor = geoHover.changeYearData[equityIndicator.variable].fillColor;
    } else {
      fillColor = undefined;
    }
  } else {
    fillColor = undefined;
  }

  let data = equityIndicatorData.filter(d => d.variable === equityIndicator.variable);

  if (geo === "State") {
    data = data.filter(d => !d.missing);
  }

  let scale = equityIndicatorScale(equityIndicator);

  const svg = d3.select(`#${equityLegendId} svg`)
    .attr('height', legendHeight(data));

  svg
    .selectAll('rect')
    .data(data, (d: any) => d.id)
    .join(
      enter => enter
        .append('rect')
        .attr('height', rectHeight)
        .attr('width', rectHeight)
        .attr('transform', function (d, i) {
          return 'translate(' + (circleStart - 6) + ', ' + (i * yStep + 1) + ')';
        })
        .attr('opacity', theme.highlightOpacity)
        .attr('fill', (d: any) => scale(d.value) as string)
        .attr("stroke", theme.darkGradientColor)
        .attr('stroke-width', 1),
      update => update
        .attr('opacity', (d: any) => scale(d.value) as string === fillColor as string || fillColor === undefined ? theme.highlightOpacity : theme.nonHighlightOpacity
        )
  );

  svg
    .selectAll('text')
    .data(data, (d: any) => d.id)
    .join(
      (enter: any) => enter
        .append('text')
        .attr('x', textStart)
        .attr('y', (d: any, i: number) => i * yStep + 11)
        .text((d: any) => d.label)
        .attr('font-size', theme.fontSize)
        .attr('fill', theme.grey.primary),
      (update: any) => update
        .attr('opacity', (d: any) => geo ? scale(d.baseYearPctBlack) as string === fillColor as string || fillColor === undefined ? 1 : theme.nonHighlightOpacity : d.id === fillColor || fillColor === undefined? 1 : theme.nonHighlightOpacity)
    );
}

function initColorSummary() {

  const svg = d3.select(`#${colorSummaryId}`)
    .append('svg')
    .attr('width', width)
    .attr('height', colorSummaryHeight + yStart );

  svg.append("line")
    .attr("x1", yAxis)
    .attr("y1", 10)
    .attr('x2', yAxis)
    .attr("y2", colorSummaryHeight - 5)
    .attr("stroke-width", 1)
    .attr("stroke", theme.grey.secondary)
    .attr("z-index", -12);
}

function updateColorSummary(pollHover: any, countiesData: GeoJSON.FeatureCollection, loadedCountiesData: boolean, selectedState: any, selectedCounty: any, indicator: Indicator ) {

  if (loadedCountiesData && selectedState.stfp !== '') {

    const data = countiesData.features.filter((d:any) => d.properties.changeYearData && d.properties.stfp === selectedState.stfp && d.properties.changeYearData.pollSummary.missingDataFlag === false)
                                      .map((d: any) => d.properties);

    const missingData = countiesData.features.filter((d:any) => d.properties.changeYearData && d.properties.stfp === selectedState.stfp && d.properties.changeYearData.pollSummary.missingDataFlag === true)
                                             .map((d: any) => d.properties);
    let axis = colorData.filter(d => d.geo === 'state' && d.indicator === indicator.variable);

    let keys;
    let summaryData;
    if (indicator.variable === "changeNoPolls") {
      keys = [...new Set(data.map((d: any) => overallChangeThresholdScale(d.changeYearData.pollSummary.status.overallChange)))].sort((a, b) => b - a);
      summaryData = d3.group(data, d => overallChangeThresholdScale(d.changeYearData.pollSummary.status.overallChange)); 
    } else {
      keys = [...new Set(data.map((d: any) => changeYearCVAPPerPollThresholdScale(d.changeYearData.pollSummary.status.changeYearCVAPPerPoll)))];
      summaryData = d3.group(data, d => changeYearCVAPPerPollThresholdScale(d.changeYearData.pollSummary.status.changeYearCVAPPerPoll));
    }

    if (missingData.length > 0) {
      keys.push(-4);
      axis.push( { id: -100, status: '', label: "Missing", overallChange: '-4', geo: 'state', indicator: indicator.variable, shortDescr: 'Missing' });
    }

    const summarizedData = [];

    for (let key in keys) {
      const keyData = summaryData.get(keys[key]);

      if (!keyData) {
        for (let r in missingData) {
          let row = missingData[parseInt(r)];
          row.y = -4;
          row.x = parseInt(r);
          row.fill = theme.missingDataFill;
          row.stroke = theme.grey.secondary;
          summarizedData.push(row);
        }
      } else {
        for (let r in keyData) {
          let row = keyData[parseInt(r)];
          row.y = keys[key];
          row.x = parseInt(r);
          if (indicator.variable === "changeNoPolls") {
            row.fill = pollFillScale(row.y.toString());
            row.stroke = pollStrokeScale2(overallChangeStrokeThresholdScale(row.changeYearData.pollSummary.status.overallChange).toString());
          } else {
            row.fill = pollFillScale(row.y.toString());
            row.stroke = pollStrokeScale2(changeYearCVAPPerPollStrokeThresholdScale(row.changeYearData.pollSummary.status.changeYearCVAPPerPoll).toString())
          }
          summarizedData.push(row);
        }
      }
    }

    const yDomain: any[] = d3.extent(keys) as [number, number];
    const xDomain: any[] = d3.extent(summarizedData.map((d: any) => d.x).filter((x: any) => x !== undefined)) as [number, number];

    let xScale = d3.scaleLinear()
      .domain(xDomain)
      .range([yAxis + 5, width - 10]);

    let yScale = d3.scaleLinear()
      .domain(yDomain)
      .range([colorSummaryHeight - 1, 8]);
  
    const svg = d3.select(`#${colorSummaryId} svg`);

    svg.selectAll('text').remove();
    svg.selectAll('circle').remove();
    svg.selectAll('.tick').remove();

    for (let d of axis) {

      svg.append("line")
        .attr("class", "tick")
        .attr("x1", yAxis - 3)
        .attr("y1", yScale(parseInt(d.overallChange)))
        .attr('x2', yAxis + 3)
        .attr("y2", yScale(parseInt(d.overallChange)))
        .attr("stroke-width", 1)
        .attr("stroke", theme.grey.secondary)
        .attr("z-index", -10);

      svg
        .append("text")
        .attr("x", 0)
        .attr("y", yScale(parseInt(d.overallChange)))
        .attr("font-size", theme.fontSize - 1)
        .attr("fill", theme.grey.primary)
        .attr("alignment-baseline", "middle")
        .attr("text-anchor", "start")
        .text(d.shortDescr);
    }

    for (let d of yDomain) {
      svg.append("line")
      .attr("class", "tick")
      .attr("x1", yAxis -5)
      .attr("y1", yScale(d))
      .attr('x2', yAxis +5)
      .attr("y2", yScale(d))
      .attr("stroke-width", 1)
      .attr("stroke", theme.grey.secondary)
      .attr("z-index", -10);
    }

    svg
      .selectAll('circle')
      .data(summarizedData, (d: any) => d.cntyfp)
      .join(
        enter => enter
          .append('circle')
          .attr('r', 5)
          .attr('cy', (d: any) => yScale(d.y))
          .attr('cx', (d: any) => xScale(d.x))
          .attr('fill', (d: any) => d.fill as string) // Add type assertion
          .attr("stroke", (d: any) => d.stroke as string) // Add type assertion
          .attr('stroke-width', 1)
          .attr('opacity', (d: any) => { 
            return selectedCounty.cntyfp != "" ? selectedCounty.cntyfp === d.cntyfp? 1 :  theme.nonHighlightOpacity: pollHover.cntyfp === undefined || pollHover.cntyfp === d.cntyfp? 1 :  theme.nonHighlightOpacity; })
      );
  }
}

function SizeTypeCounty ({indicator} : {indicator: Indicator}) {

  CVAPListener();

  return (
    <ComponentGroupInner title={IndicatorSizeLabel(indicator)}>
      <div id={sizeLegendId}></div>
    </ComponentGroupInner>
  );
}

function ColorTypeCounty ({indicator}: {indicator: Indicator}) {

  CVAPListener();

  return (
    <ComponentGroupInner title={IndicatorColorLabel(indicator)} className='BorderTop'>
      <div id={pollLegendId}></div>
    </ComponentGroupInner>
  );
}

function ColorTypeTract () {
  return (
    <ComponentGroupInner title="Poll status">
      <div id={pollLegendId}></div>
    </ComponentGroupInner>
  );
}

function ColorSummaryTract ({indicator}: {indicator: Indicator}) {
  return (
    <ComponentGroupInner title={indicator.descr} className='BorderTop'>
      <div id={colorSummaryId}></div>
    </ComponentGroupInner>
  );
}

export function CountyLegend ({pollHover, indicator, showEquityIndicator} : {pollHover: any, indicator: Indicator, showEquityIndicator: boolean}) {

  // Initiate legends
  useEffect(() => {
    initLegend(pollLegendId);
    initLegend(sizeLegendId);
  }, []);

  // Initiate legends
  useEffect(() => {
    updatePollLegend('state', pollHover, indicator, showEquityIndicator);
    updateSizeLegend(pollHover, indicator, showEquityIndicator);
  }, [pollHover, indicator, showEquityIndicator]);

  return (
    <>
      <SizeTypeCounty indicator={indicator} />
      <ColorTypeCounty indicator={indicator} />
    </>
  );
}

export function TractLegend ({pollHover, indicator, selectedCounty, changeYear, showEquityIndicator} : 
                            {pollHover: any, indicator: Indicator, selectedCounty: any, changeYear: ChangeYear, showEquityIndicator: boolean}) {
  // Initiate legends
  useEffect(() => {
    initLegend(pollLegendId);
  }, []);

  // Initiate legends
  useEffect(() => {
    updatePollLegend('county', pollHover, indicator, showEquityIndicator);
  }, [pollHover, showEquityIndicator]);

  return (
    <>
      <ColorTypeTract />
      {selectedCounty.changeYearData? <UpdateMissingDataWarning status={selectedCounty.changeYearData.pollSummary.status} changeYear={changeYear} stfp={selectedCounty.stfp}/>: null }
    </>
  );
}

export function CountySummary({pollHover, indicator, countiesData, loadedCountiesData, selectedState, selectedCounty}: 
                              {pollHover: any, indicator: Indicator, countiesData: GeoJSON.FeatureCollection, loadedCountiesData: boolean,
                              selectedState: any, selectedCounty: any}) {

  // Initiate legends
  useEffect(() => {
  initColorSummary();
  }, []);

  // Initiate legends
  useEffect(() => {
  updateColorSummary(pollHover, countiesData, loadedCountiesData, selectedState, selectedCounty, indicator);
  }, [pollHover, countiesData, selectedState, selectedCounty, indicator]);

  return (
    <ColorSummaryTract indicator={indicator}/>
  );
}


export function EquityLegend ({equityIndicator, geoHover, geo} : {equityIndicator: EquityIndicator, geoHover: any, geo: string}) {

  useEffect(() => {
    initLegend(equityLegendId);
  }, []);

  // Initiate legends
  useEffect(() => {
    updateEquityLegend(equityIndicator, geoHover, geo);
  }, [equityIndicator, geoHover]);

  CVAPListener();

  return (
    <div className="Legend">
      <ComponentGroupInner title={EquityIndicatorLabel(equityIndicator)}>
        <div id={equityLegendId}></div>
      </ComponentGroupInner>
    </div>
  );
}